import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, CheckCircle2 } from "lucide-react";

const EMPTY = {
  agent_name: "",
  brokerage_name: "",
  phone: "",
  email: "",
  property_address: "",
  listing_price: "",
  buy_side_commission: "",
  sale_side_commission: "",
  payoff_amount: "",
};

export default function NetsheetForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = [
      "agent_name",
      "brokerage_name",
      "phone",
      "email",
      "property_address",
      "listing_price",
      "buy_side_commission",
      "sale_side_commission",
    ];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/netsheet/request", form);
      setSent(true);
      toast.success("Thank you — We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-paper border border-soft p-10 md:p-14 text-center" data-testid="netsheet-success">
        <CheckCircle2 size={36} className="text-sand mx-auto" strokeWidth={1.25} />
        <p className="overline mt-6">Request received</p>
        <h3 className="mt-3 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
          Thank you — We'll be in touch soon.
        </h3>
        <p className="mt-4 text-soft max-w-xl mx-auto">
          Your netsheet request has been submitted. We'll prepare your estimate and reach out
          shortly with a detailed breakdown for your transaction.
        </p>
        <button
          type="button"
          className="btn-outline mt-8"
          onClick={() => { setForm(EMPTY); setSent(false); }}
          data-testid="netsheet-new-btn"
        >
          Submit another netsheet
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="bg-paper border border-soft p-8 md:p-12" data-testid="netsheet-form">
      <div className="flex items-start gap-4 mb-8">
        <FileText size={28} className="text-sand shrink-0 mt-1" strokeWidth={1.25} />
        <div>
          <p className="overline">Request a netsheet</p>
          <h3 className="mt-2 text-2xl md:text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>
            Get a quick estimate.
          </h3>
          <p className="mt-2 text-soft text-sm max-w-xl">
            Share a few transaction details and We'll prepare a netsheet for you. Required
            fields are marked with an asterisk.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className="overline">Agent name *</label>
          <input
            className="input-editorial mt-2"
            value={form.agent_name}
            onChange={update("agent_name")}
            placeholder="Your full name"
            data-testid="netsheet-agent-name"
            required
          />
        </div>
        <div>
          <label className="overline">Brokerage name *</label>
          <input
            className="input-editorial mt-2"
            value={form.brokerage_name}
            onChange={update("brokerage_name")}
            placeholder="Brokerage / firm"
            data-testid="netsheet-brokerage"
            required
          />
        </div>
        <div>
          <label className="overline">Phone *</label>
          <input
            type="tel"
            className="input-editorial mt-2"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(555) 555-5555"
            data-testid="netsheet-phone"
            required
          />
        </div>
        <div>
          <label className="overline">Email *</label>
          <input
            type="email"
            className="input-editorial mt-2"
            value={form.email}
            onChange={update("email")}
            placeholder="you@brokerage.com"
            data-testid="netsheet-email"
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="overline">Property address *</label>
          <input
            className="input-editorial mt-2"
            value={form.property_address}
            onChange={update("property_address")}
            placeholder="Street, city, state, zip"
            data-testid="netsheet-address"
            required
          />
        </div>
        <div>
          <label className="overline">Approx. listing price *</label>
          <input
            className="input-editorial mt-2"
            value={form.listing_price}
            onChange={update("listing_price")}
            placeholder="$1,250,000"
            data-testid="netsheet-listing-price"
            required
          />
        </div>
        <div>
          <label className="overline">Approx. payoff <span className="lowercase text-soft normal-case">(optional)</span></label>
          <input
            className="input-editorial mt-2"
            value={form.payoff_amount}
            onChange={update("payoff_amount")}
            placeholder="Amount still owed on the property"
            data-testid="netsheet-payoff"
          />
        </div>
        <div>
          <label className="overline">Buy-side commission *</label>
          <input
            className="input-editorial mt-2"
            value={form.buy_side_commission}
            onChange={update("buy_side_commission")}
            placeholder="e.g. 2.5% or $25,000"
            data-testid="netsheet-buy-commission"
            required
          />
        </div>
        <div>
          <label className="overline">Sale-side commission *</label>
          <input
            className="input-editorial mt-2"
            value={form.sale_side_commission}
            onChange={update("sale_side_commission")}
            placeholder="e.g. 2.5% or $25,000"
            data-testid="netsheet-sale-commission"
            required
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary mt-10 w-full md:w-auto disabled:opacity-50"
        data-testid="netsheet-submit"
      >
        {submitting ? (
          <><Loader2 size={16} className="animate-spin mr-2" /> Submitting…</>
        ) : (
          "Request my netsheet"
        )}
      </button>
    </form>
  );
}
